import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setManualMeshingSensorOffset,
  resetManualMeshingSensorOffset,
  isResourceInState,
  applyActionToSensorAnalysisState,
} from "../../redux/sensorAnalysis";
import { Button } from "react-bootstrap";
import { publish, windowEvents } from "../events";
import { useMeshSensorsCRUD, useSplitSensorsForMeshing } from "../CustomHooks";
import { transformFromModelSpace, unique } from "../utils";
import { reduxLocalStorage } from "../utility/storage";
import {
  getAllMirrorWindowSensors,
  getWindowLocalCoordSystem,
  isTempSensor,
} from "./utils";
import { useMemo } from "react";
import { clearSelections, useWindowSelection } from "../objectSelection";
import ParameterInput from "../general/ParameterInput";
import { actions, resources } from "../../redux/sensorAnalysisConstants";

const getNewSensorName = (allWindows, allSensors, windowId, index) => {
  const windowName = allWindows.find((window) => window.id === windowId)?.name;

  const windowNoOfSensors = allSensors?.filter(
    (sensor) => sensor.surfaceId === windowId
  )?.length;

  const sensorNo = (windowNoOfSensors ?? 0) + index + 1;
  return windowName + "__M_" + sensorNo;
};

const toApiSensorFormat = (
  sensor,
  project_id,
  allWindows,
  allSensors,
  index = 0
) => {
  const position = transformFromModelSpace(sensor.position);
  const windowId = sensor.windowId;
  const name = getNewSensorName(allWindows, allSensors, windowId, index);

  return {
    surfaceId: windowId,
    customerId: 114,
    sensorType: "solar",
    projectId: Number(project_id),
    utmx: position[0],
    utmy: position[1],
    utmz: position[2],
    name: name,
  };
};

const ManualMeshingPlacementControls = () => {
  const project_id = reduxLocalStorage.getItem("project_id");
  const dispatch = useDispatch();
  const { sensors, sensorOffset, selectedSensors } = useSelector(
    (state) => state.sensorAnalysis.manualMeshing.state
  );
  const allWindows = useSelector((state) => state.windowData.allWindows);
  const allSensors = useSelector((state) => state.sensorAnalysis.sensorsData);
  const sensoranalysisState = useSelector(
    (state) => state.sensorAnalysis.sensorAnalysisState
  );
  const { selectedPolygonYUp } = useWindowSelection();

  const selectedWindowsIds = useSelector(
    (state) => state.objectSelection.selections.windows
  );
  const [toBeDeletedSensors, setToBeDeletedSensors] = useState([]);
  const { create, remove, updatePosition } = useMeshSensorsCRUD();

  const resetEverythingAfterSubmitOrCancel = () => {
    dispatch(clearSelections());
    dispatch(resetManualMeshingSensorOffset());
    setToBeDeletedSensors([]);
    dispatch(applyActionToSensorAnalysisState(actions.ON_CANCEL));
  };

  const setSensorOffset = useCallback(
    (offset) => dispatch(setManualMeshingSensorOffset(offset)),
    [dispatch]
  );

  const isMoving = isResourceInState(
    sensoranalysisState,
    resources.MOVE_MESHING_CONTROLS
  );
  const isAdding = isResourceInState(
    sensoranalysisState,
    resources.ADD_MESHING_CONTROLS
  );

  const windowCoordSystem = useMemo(
    () => getWindowLocalCoordSystem(selectedPolygonYUp?.polygon),
    [selectedPolygonYUp]
  );
  const mirrorWindowsProps = [
    allWindows,
    selectedPolygonYUp,
    selectedWindowsIds,
    sensors,
    windowCoordSystem,
  ];
  const mirrorWindowsSensors = useMemo(
    () => getAllMirrorWindowSensors(...mirrorWindowsProps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    mirrorWindowsProps
  );

  const handleCancel = () => resetEverythingAfterSubmitOrCancel();

  const { sensorsToMoving } = useSplitSensorsForMeshing();
  const handleSubmit = async () => {
    try {
      const newSensors = sensors?.filter((sensor) => isTempSensor(sensor));

      if (toBeDeletedSensors?.length > 0) await remove(toBeDeletedSensors);

      if (newSensors?.length > 0)
        await create(
          newSensors.map((sensor, index) =>
            toApiSensorFormat(sensor, project_id, allWindows, allSensors, index)
          )
        );

      if (newSensors?.length > 0 && mirrorWindowsSensors?.length > 0) {
        const mirrorSensors = mirrorWindowsSensors?.reduce((acc, window) => {
          acc.push(...window.sensors);
          return acc;
        }, []);

        await create(
          mirrorSensors?.map((sensor, index) =>
            toApiSensorFormat(sensor, project_id, allWindows, allSensors, index)
          )
        );
      }

      const updatedSensors = sensors?.filter((sensor) => !isTempSensor(sensor));
      if (isAdding && updatedSensors?.length > 0)
        await updatePosition(updatedSensors);

      if (isMoving && sensorsToMoving?.length > 0)
        await updatePosition(sensorsToMoving);
    } catch (error) {
    } finally {
      resetEverythingAfterSubmitOrCancel();
    }
  };

  const handleDelete = () => {
    setToBeDeletedSensors(
      unique([
        ...toBeDeletedSensors,
        ...(selectedSensors?.filter((sensor) => !isTempSensor(sensor)) ?? []),
      ])
    );
    publish(windowEvents.SENSORS_DELETE, selectedSensors);
  };

  const visible = isResourceInState(
    sensoranalysisState,
    resources.MANUAL_MESHING_PLACEMENT_CONTROLS
  );

  if (!visible) return null;

  const isSubmitDisabled =
    !sensors?.length > 0 && !toBeDeletedSensors?.length > 0;
  return (
    <div className="execution-cta-container">
      <div className="flex">
        <div className={isMoving ? "flex-col" : "flex-row"}>
          <Button
            variant="secondary"
            onClick={handleCancel}
            style={{
              padding: "8px 16px",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              padding: "8px 16px",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: !isSubmitDisabled ? "pointer" : "not-allowed",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Submit
          </Button>
        </div>
        {isAdding && (
          <Button
            onClick={handleDelete}
            disabled={selectedSensors?.length === 0}
            variant="danger"
            style={{
              padding: "8px 16px",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Delete
          </Button>
        )}
        {isMoving && (
          <div>
            <ParameterInput
              label={"Horizontal Offset"}
              value={sensorOffset?.x}
              min={-100}
              max={100}
              noOfDecimals={2}
              step={1}
              onChange={(_, value) =>
                setSensorOffset({ ...sensorOffset, x: value })
              }
            />
            <ParameterInput
              label={"Vertical Offset"}
              value={sensorOffset?.y}
              min={-100}
              max={100}
              noOfDecimals={2}
              step={1}
              onChange={(_, value) =>
                setSensorOffset({ ...sensorOffset, y: value })
              }
            />
            <ParameterInput
              label={"Depth Offset"}
              value={sensorOffset?.z}
              min={-3}
              max={3}
              step={0.1}
              noOfDecimals={2}
              onChange={(_, value) => {
                setSensorOffset({ ...sensorOffset, z: value });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualMeshingPlacementControls;
